import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MARK_COLORS, WIDTHS, contentBox, normalise, eraserHits, renderStrokes } from './drawCore';
import { ICONS, TOOL_NAMES, COLOR_NAMES, colorOf, paintSurface, downloadCanvas, stamp, useCanvas } from './Whiteboard';
import './draw.css';

/**
 * Marks on somebody's shared screen, which never touch the screen itself.
 *
 * Gordon, 22 October: "any user can highlight or mark certain areas of the
 * shared screen with everybody seeing those marks without those marks
 * editing whatever is being shown." So the marks are a SEPARATE LAYER: a
 * transparent canvas laid exactly over the picture in the <video>, on every
 * page that shows that share - the presenter's own preview included. The
 * presenter's slides, document or application are never drawn on, because
 * nothing here has any way to reach them; what everybody sees is the share
 * with a sheet of glass over it.
 *
 * EXACTLY OVER THE PICTURE, NOT OVER THE ELEMENT. The video is object-fit:
 * contain, so a 4:3 slide in a 16:10 tile has bars down both sides - and how
 * wide those bars are depends on each person's window. The layer is sized to
 * the picture alone (drawCore's contentBox, from videoWidth / videoHeight
 * and the element's size), and coordinates are fractions of the picture, so
 * a circle round a number on one screen is round the same number on all of
 * them. It is recomputed whenever the element or the picture changes size -
 * a window resized, full screen, the presenter switching to a window of a
 * different shape.
 *
 * WHEN THE SHARE STOPS, THE MARKS GO: they were about that picture, and
 * SessionPage forgets the surface the moment the share ends.
 *
 * Only while Annotate is on does the layer take the pointer; the rest of the
 * time it shows the marks and lets every click through to the video.
 */

const MARK_TOOLS = ['pen', 'highlighter', 'arrow', 'rect', 'laser', 'eraser'];

const Annotate = ({ engine, target, getVideo, active, presenter = false, onClose, whose = 'this screen' }) => {
  const [tool, setTool] = useState('pen');
  const [color, setColor] = useState('red');
  const [place, setPlace] = useState(null);
  const [clearing, setClearing] = useState(null);
  const [saved, setSaved] = useState(false);
  const canvasRef = useRef(null);
  const strokeRef = useRef(null);
  const erasingRef = useRef(false);
  const downRef = useRef(false);

  /*
   * WHERE THE PICTURE IS, relative to the tile the layer is positioned in.
   * Checked on resize, on the video's own resize event (the picture changed
   * shape), and on a slow tick for the cases neither of those catches - a
   * tile that moved without changing size when somebody joined.
   */
  useEffect(() => {
    let alive = true;
    let last = '';
    const measure = () => {
      if (!alive) return;
      const v = getVideo();
      const c = canvasRef.current;
      const parent = c?.offsetParent;
      if (!v || !c || !parent || !(v.videoWidth > 0) || v.offsetParent === null) {
        if (last !== 'none') { last = 'none'; setPlace(null); }
        return;
      }
      const vr = v.getBoundingClientRect();
      const pr = parent.getBoundingClientRect();
      const cb = contentBox(vr.width, vr.height, v.videoWidth, v.videoHeight);
      const next = {
        left: Math.round((vr.left - pr.left - parent.clientLeft + cb.x) * 10) / 10,
        top: Math.round((vr.top - pr.top - parent.clientTop + cb.y) * 10) / 10,
        width: Math.round(cb.w * 10) / 10,
        height: Math.round(cb.h * 10) / 10
      };
      const key = `${next.left},${next.top},${next.width},${next.height}`;
      if (key !== last) { last = key; setPlace(next); }
    };
    measure();
    const v = getVideo();
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
    if (v && ro) ro.observe(v);
    v?.addEventListener('resize', measure);
    v?.addEventListener('loadedmetadata', measure);
    window.addEventListener('resize', measure);
    document.addEventListener('fullscreenchange', measure);
    const tick = setInterval(measure, 400);
    return () => {
      alive = false;
      ro?.disconnect();
      v?.removeEventListener('resize', measure);
      v?.removeEventListener('loadedmetadata', measure);
      window.removeEventListener('resize', measure);
      document.removeEventListener('fullscreenchange', measure);
      clearInterval(tick);
    };
  }, [getVideo]);

  const holderRef = canvasRef;
  const paint = useCallback((ctx, w, h) => {
    paintSurface(ctx, engine, target, { x: 0, y: 0, w, h });
  }, [engine, target]);
  const { sizeRef } = useCanvas(canvasRef, holderRef, engine, target, paint);

  const box = () => ({ x: 0, y: 0, w: sizeRef.current.w, h: sizeRef.current.h });
  const at = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return normalise(e.clientX - r.left, e.clientY - r.top, box());
  };
  const aspect = () => (sizeRef.current.h > 0 ? sizeRef.current.w / sizeRef.current.h : 16 / 9);

  const eraseAt = (pt) => {
    const ids = eraserHits(engine.strokes(target), pt, 0.025, aspect());
    if (ids.length) engine.erase(ids);
  };

  const down = (e) => {
    if (!active || e.button > 0) return;
    e.preventDefault();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* fine */ }
    downRef.current = true;
    const pt = at(e);
    if (tool === 'laser') { engine.laser(target, pt); return; }
    if (tool === 'eraser') { erasingRef.current = true; eraseAt(pt); return; }
    strokeRef.current = engine.begin(target, { tool, color, width: WIDTHS[tool], point: pt });
  };

  const move = (e) => {
    if (!active) return;
    // The laser follows a mouse without a button held - that is how a laser
    // pointer is used - and a finger or a pen while it touches.
    if (tool === 'laser' && (downRef.current || e.pointerType === 'mouse')) {
      engine.laser(target, at(e));
      return;
    }
    if (!strokeRef.current && !erasingRef.current) return;
    const list = typeof e.nativeEvent?.getCoalescedEvents === 'function' ? e.nativeEvent.getCoalescedEvents() : [];
    for (const ev of (list.length ? list : [e])) {
      const pt = at(ev);
      if (strokeRef.current) engine.extend(strokeRef.current, pt);
      else eraseAt(pt);
    }
  };

  const up = () => {
    if (strokeRef.current) engine.end(strokeRef.current);
    strokeRef.current = null;
    erasingRef.current = false;
    downRef.current = false;
  };

  /** The frame as it is now, with the marks on it, as a picture. */
  const snapshot = async () => {
    const v = getVideo();
    if (!v || !(v.videoWidth > 0)) return;
    const c = document.createElement('canvas');
    c.width = v.videoWidth;
    c.height = v.videoHeight;
    const ctx = c.getContext('2d');
    try { ctx.drawImage(v, 0, 0, c.width, c.height); } catch { /* a frame not ready */ }
    renderStrokes(ctx, engine.strokes(target), { x: 0, y: 0, w: c.width, h: c.height }, colorOf);
    if (await downloadCanvas(c, `marked-up-screen-${stamp()}.png`)) {
      setSaved(true);
      setTimeout(() => setSaved(false), 2200);
    }
  };

  const clearAll = () => {
    // The presenter owns the picture, so clearing it is theirs without a
    // question; anybody else is asked first, because it clears everybody's.
    if (presenter) { engine.clear(target, 'all'); setClearing(null); return; }
    setClearing('confirm');
  };

  // Hidden by visibility rather than display while there is no picture, so
  // the layer keeps an offsetParent to measure against when one arrives.
  return (
    <>
      <canvas ref={canvasRef} className="dr-overlay" data-target={target}
        data-active={active || undefined} aria-hidden={!active}
        style={place ? { left: place.left, top: place.top, width: place.width, height: place.height }
          : { visibility: 'hidden', left: 0, top: 0, width: 0, height: 0 }}
        onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}
        onPointerLeave={() => { if (!downRef.current) up(); }} />
      {active && (
        <div className="dr-tools dr-annot-tools" role="toolbar" aria-label={`Marking up ${whose}`}>
          <span className="dr-annot-note">
            <span className="dr-annot-dot" aria-hidden="true" />
            Marks show for everybody — they never change {whose}.
          </span>
          <div className="dr-group" role="radiogroup" aria-label="Tool">
            {MARK_TOOLS.map((t) => (
              <button key={t} type="button" className="dr-tool" role="radio" aria-checked={tool === t}
                data-tool={t} onClick={() => setTool(t)} aria-label={TOOL_NAMES[t]} title={TOOL_NAMES[t]}>
                {ICONS[t]}
              </button>
            ))}
          </div>
          <span className="dr-sep" aria-hidden="true" />
          <div className="dr-group" role="radiogroup" aria-label="Colour">
            {MARK_COLORS.map((c) => (
              <button key={c} type="button" className="dr-swatch" role="radio" aria-checked={color === c}
                data-color={c} onClick={() => setColor(c)} aria-label={COLOR_NAMES[c]} title={COLOR_NAMES[c]} />
            ))}
          </div>
          <span className="dr-sep" aria-hidden="true" />
          <div className="dr-group">
            <button type="button" className="dr-tool dr-undo" onClick={() => engine.undo(target)}
              aria-label="Undo my last mark" title="Undo my last mark">{ICONS.undo}</button>
            <div className="dr-pop-wrap">
              <button type="button" className="dr-tool dr-clear" aria-expanded={clearing !== null}
                onClick={() => setClearing(clearing ? null : 'menu')} aria-label="Clear marks" title="Clear marks">
                {ICONS.clear}
              </button>
              {clearing && (
                <div className="dr-pop dr-pop-up" role="dialog" aria-label="Clear the marks">
                  {clearing === 'menu' ? (
                    <>
                      <button type="button" className="dr-pop-item dr-clear-mine"
                        onClick={() => { engine.clear(target, 'mine'); setClearing(null); }}>Clear mine</button>
                      <button type="button" className="dr-pop-item dr-clear-all" onClick={clearAll}>
                        {presenter ? 'Clear all marks' : "Clear everyone's…"}
                      </button>
                    </>
                  ) : (
                    <>
                      <p className="dr-pop-q">Clear everybody's marks on {whose}?</p>
                      <div className="dr-pop-row">
                        <button type="button" className="dr-pop-danger dr-clear-all-yes"
                          onClick={() => { engine.clear(target, 'all'); setClearing(null); }}>Clear all</button>
                        <button type="button" className="dr-pop-item" onClick={() => setClearing(null)}>Cancel</button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
            <button type="button" className="dr-tool dr-snap" onClick={snapshot}
              aria-label="Save a marked-up snapshot" title="Save a marked-up snapshot (PNG)">{ICONS.save}</button>
            {saved && <span className="dr-saved" role="status">Saved</span>}
            <button type="button" className="dr-icon-btn dr-annot-done" onClick={onClose}>Done</button>
          </div>
        </div>
      )}
    </>
  );
};

export default Annotate;
